import {
    Card,
    CardContent,
    Typography,
    Stack,
    Divider,
    Button,
    Chip,
    Box,
    Tooltip,
    IconButton,
    Avatar,
    Paper,
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router';
import {
    Business as BusinessIcon,
    People as PeopleIcon,
    QrCodeScanner as QrCodeIcon,
    Visibility as VisibilityIcon,
    TrendingUp as TrendingUpIcon,
    Edit as EditIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    Upgrade as UpgradeIcon,
    Print as PrintIcon,
} from '@mui/icons-material';

export default function SponsorsRegCard({ sponsorData, onUpdateLimit, onSendEmail }) {
    const navigate = useNavigate();

    // Generate unique gradient color based on company name
    const getGradientColor = name => {
        if (!name) return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
        const hash = name.split('').reduce((acc, char) => {
            return char.charCodeAt(0) + ((acc << 5) - acc);
        }, 0);
        const colors = [
            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
            'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
        ];
        return colors[Math.abs(hash) % colors.length];
    };

    const gradientColor = getGradientColor(sponsorData?.company);

    const totalRegistrations = sponsorData?.registrationData?.total || 0;
    const allowedRegistrations = sponsorData?.registrationData?.totalAllowedRegistration || 0;
    const totalEntry = sponsorData?.scannedData?.total?.entry || 0;
    const totalPrints = sponsorData?.printedData?.total || 0;

    return (
        <Card
            sx={{
                height: '100%',
                minHeight: '320px',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                position: 'relative',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    borderColor: 'primary.main',
                },
            }}
        >
            {/* Header with Gradient */}
            <Box
                sx={{
                    background: gradientColor,
                    p: 2.5,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
                        pointerEvents: 'none',
                    },
                }}
            >
                <Stack direction='column' justifyContent='space-between' alignItems='flex-start' spacing={2}>
                    <Box sx={{ flex: 1, position: 'relative', zIndex: 1 }}>
                        <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 1 }}>
                            <Avatar
                                sx={{
                                    width: 48,
                                    height: 48,
                                    bgcolor: 'rgba(255,255,255,0.25)',
                                    backdropFilter: 'blur(10px)',
                                    border: '2px solid rgba(255,255,255,0.3)',
                                }}
                            >
                                <BusinessIcon />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant='h6'
                                    fontWeight={700}
                                    noWrap
                                    sx={{
                                        textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                        mb: 0.5,
                                        fontSize: '1.1rem',
                                    }}
                                >
                                    {sponsorData?.company || 'N/A'}
                                </Typography>
                                <Stack direction='row' spacing={1} alignItems='center'>
                                    <Typography
                                        variant='caption'
                                        sx={{
                                            opacity: 0.95,
                                            fontWeight: 500,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            fontSize: '0.7rem',
                                        }}
                                    >
                                        Sponsor
                                    </Typography>
                                    {sponsorData?.StallNo && (
                                        <>
                                            <Stack direction='row' spacing={0.5} alignItems='center'>
                                                <LocationIcon sx={{ fontSize: '0.75rem' }} />
                                                <Typography
                                                    variant='caption'
                                                    sx={{
                                                        opacity: 0.95,
                                                        fontWeight: 500,
                                                        fontSize: '0.7rem',
                                                    }}
                                                >
                                                    Stall {sponsorData.StallNo}
                                                </Typography>
                                            </Stack>
                                        </>
                                    )}
                                </Stack>
                            </Box>
                        </Stack>
                    </Box>
                    <Stack direction='row' spacing={0.5} sx={{ position: 'relative', zIndex: 1 }}>
                        {onSendEmail && (
                            <Tooltip title='Send Email' arrow>
                                <IconButton
                                    size='small'
                                    sx={{
                                        color: 'white',
                                        bgcolor: 'rgba(255,255,255,0.15)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.25)',
                                            transform: 'scale(1.1)',
                                        },
                                        transition: 'all 0.2s',
                                    }}
                                    onClick={e => {
                                        e.stopPropagation();
                                        onSendEmail();
                                    }}
                                >
                                    <EmailIcon fontSize='small' />
                                </IconButton>
                            </Tooltip>
                        )}
                        {onUpdateLimit && (
                            <Tooltip title='Upgrade Registration Limit' arrow>
                                <IconButton
                                    size='small'
                                    sx={{
                                        color: 'white',
                                        bgcolor: 'rgba(255,255,255,0.15)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.25)',
                                            transform: 'scale(1.1)',
                                        },
                                        transition: 'all 0.2s',
                                    }}
                                    onClick={e => {
                                        e.stopPropagation();
                                        onUpdateLimit(sponsorData);
                                    }}
                                >
                                    <UpgradeIcon fontSize='small' />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title='View Details' arrow>
                            <IconButton
                                size='small'
                                sx={{
                                    color: 'white',
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.25)',
                                        transform: 'scale(1.1)',
                                    },
                                    transition: 'all 0.2s',
                                }}
                                onClick={() => navigate(`${sponsorData.sponsorId}`, { state: { sponsorData } })}
                            >
                                <VisibilityIcon fontSize='small' />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Box>

            <CardContent
                sx={{
                    flexGrow: 1,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    pb: 2,
                }}
            >
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    {/* Metrics Grid */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: 1.25,
                            mb: 1.5,
                        }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                p: 1.25,
                                bgcolor: 'rgba(102, 126, 234, 0.08)',
                                border: '1px solid',
                                borderColor: 'rgba(102, 126, 234, 0.2)',
                                borderRadius: 1.5,
                                textAlign: 'center',
                            }}
                        >
                            <Stack
                                direction='row'
                                spacing={0.5}
                                alignItems='center'
                                justifyContent='center'
                                sx={{ mb: 0.5 }}
                            >
                                <PeopleIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                                <Typography variant='caption' color='text.secondary' fontWeight={600}>
                                    Registrations
                                </Typography>
                            </Stack>
                            <Typography variant='h6' fontWeight={700} color='primary.main'>
                                {totalRegistrations}
                            </Typography>
                            <Typography variant='caption' color='text.secondary' fontSize='0.65rem'>
                                / {allowedRegistrations}
                            </Typography>
                        </Paper>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 1.25,
                                bgcolor: 'rgba(76, 175, 80, 0.08)',
                                border: '1px solid',
                                borderColor: 'rgba(76, 175, 80, 0.2)',
                                borderRadius: 1.5,
                                textAlign: 'center',
                            }}
                        >
                            <Stack
                                direction='row'
                                spacing={0.5}
                                alignItems='center'
                                justifyContent='center'
                                sx={{ mb: 0.5 }}
                            >
                                <QrCodeIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
                                <Typography variant='caption' color='text.secondary' fontWeight={600}>
                                    Entries
                                </Typography>
                            </Stack>
                            <Typography variant='h6' fontWeight={700} color='success.main'>
                                {totalEntry}
                            </Typography>
                        </Paper>
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: 1.25,
                            mb: 1.5,
                        }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                p: 1.25,
                                bgcolor: 'rgba(255, 152, 0, 0.08)',
                                border: '1px solid',
                                borderColor: 'rgba(255, 152, 0, 0.2)',
                                borderRadius: 1.5,
                                textAlign: 'center',
                            }}
                        >
                            <Stack
                                direction='row'
                                spacing={0.5}
                                alignItems='center'
                                justifyContent='center'
                                sx={{ mb: 0.5 }}
                            >
                                <PrintIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />
                                <Typography variant='caption' color='text.secondary' fontWeight={600}>
                                    Prints
                                </Typography>
                            </Stack>
                            <Typography variant='h6' fontWeight={700} color='warning.main'>
                                {totalPrints}
                            </Typography>
                        </Paper>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 1.25,
                                bgcolor: 'rgba(156, 39, 176, 0.08)',
                                border: '1px solid',
                                borderColor: 'rgba(156, 39, 176, 0.2)',
                                borderRadius: 1.5,
                                textAlign: 'center',
                            }}
                        >
                            <Stack
                                direction='row'
                                spacing={0.5}
                                alignItems='center'
                                justifyContent='center'
                                sx={{ mb: 0.5 }}
                            >
                                <TrendingUpIcon sx={{ fontSize: '1rem', color: 'secondary.main' }} />
                                <Typography variant='caption' color='text.secondary' fontWeight={600}>
                                    Usage
                                </Typography>
                            </Stack>
                            <Typography variant='h6' fontWeight={700} color='secondary.main'>
                                {allowedRegistrations > 0
                                    ? `${Math.round((totalRegistrations / allowedRegistrations) * 100)}%`
                                    : '0%'}
                            </Typography>
                        </Paper>
                    </Box>

                    {/* Action Button */}
                    <Box sx={{ mt: 'auto', pt: 1.5 }}>
                        <Button
                            variant='contained'
                            fullWidth
                            startIcon={<VisibilityIcon />}
                            onClick={() => navigate(`${sponsorData.sponsorId}`, { state: { sponsorData } })}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                py: 1,
                            }}
                        >
                            View Details
                        </Button>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}
